/**
 * Display-name mappings for audience tiers.
 *
 * Lives in `@/lib/billing/` so non-billing surfaces (audience cards, hero
 * banner, audience edit page) can import without reaching into a settings
 * path. Plan-tier display names remain in `@/pages/SettingsPage/components/
 * billing/displayNames.ts` because they are billing-specific.
 *
 * IMPORTANT: These names mirror the canonical mapping owned by the backend's
 * `BillingConfigService` (see `apps/backend/src/billing/billing-config.service.ts`).
 * Internal slugs (`tier1`/`tier2`/`tier3`) MUST NEVER change. UI copy lives
 * here and on the backend constant; keep both in sync.
 */

import i18n from '@/i18n'

export const AUDIENCE_TIER_DISPLAY_NAMES: Record<string, string> = {
  tier1: 'Basic',
  tier2: 'Premium',
  tier3: 'Exclusive',
  // Legacy values, still in DB pre-migration. Map to the closest new label.
  standard: 'Basic',
  premium: 'Premium',
}

const AUDIENCE_TIER_I18N_SLUG: Record<string, 'basic' | 'premium' | 'exclusive'> = {
  tier1: 'basic',
  tier2: 'premium',
  tier3: 'exclusive',
  standard: 'basic',
  premium: 'premium',
}

export function audienceTierDisplayName(tier: string | null | undefined): string {
  if (!tier) {
    return i18n.t('billing.audienceTiers.standard', { ns: 'app' })
  }
  const slug = AUDIENCE_TIER_I18N_SLUG[tier]
  if (!slug) {
    return AUDIENCE_TIER_DISPLAY_NAMES[tier] ?? tier
  }
  return i18n.t(`billing.audienceTiers.${slug}`, { ns: 'app' })
}
